import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Container,
  Grid,
  Typography,
  TextField,
  Chip,
  InputAdornment,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Article as ArticleIcon,
  Category as CategoryIcon,
  CalendarToday as DateIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSafeBack } from '../hooks/useSafeBack';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';
import { learningService, LearningModule } from '../services/learningService';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';

// Using LearningModule type from service instead of Information interface

const categories = [
  'Mental Health Resources',
  'Coping Strategies',
  'Campus Services',
  'Emergency Contacts',
  'Self-Care Tips',
  'Academic Support',
  'Wellness Programs'
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`info-tabpanel-${index}`}
      aria-labelledby={`info-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const InformationPage: React.FC = () => {
  const navigate = useNavigate();
  const goBack = useSafeBack();
  const { user } = useAuth();
  const [articles, setArticles] = useState<LearningModule[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState<LearningModule | null>(null);
  const [viewDialog, setViewDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    category: '',
    tags: ''
  });

  const isAdmin = user?.userType === 'admin';
  const isProvider = user?.userType === 'provider';
  const canManage = isAdmin || isProvider;

  // Helper function to strip HTML tags from content
  const stripHtmlTags = (html: string): string => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  useEffect(() => {
    loadArticles();
  }, []);

  const loadArticles = async () => {
    try {
      setLoading(true);
      
      // Fetch real data from the learning modules API
      const response = await learningService.getModules(
        0, // page
        50, // size - get more items
        undefined, // categoryId
        searchTerm || undefined, // search
        undefined // difficultyLevel
      );
      
      setArticles(response.content);
    } catch (error) {
      console.error('Error loading articles:', error);
      toast.error('Failed to load information resources');
    } finally {
      setLoading(false);
    }
  };

  const handleViewArticle = (article: LearningModule) => {
    setSelectedArticle(article);
    setViewDialog(true);
    // In real implementation, track view count
  };

  const handleEditArticle = (article: LearningModule) => {
    setFormData({
      title: article.title,
      content: article.content,
      category: article.categoryName || '',
      tags: article.tags?.join(', ') || ''
    });
    setSelectedArticle(article);
    setEditDialog(true);
  };

  const handleSaveArticle = async () => {
    try {
      // API call would go here
      toast.success(selectedArticle ? 'Article updated' : 'Article created');
      setEditDialog(false);
      loadArticles();
    } catch (error) {
      toast.error('Failed to save article');
    }
  };

  const handleDeleteArticle = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this article?')) {
      try {
        // API call would go here
        toast.success('Article deleted');
        loadArticles();
      } catch (error) {
        toast.error('Failed to delete article');
      }
    }
  };

  const filteredArticles = articles.filter(article => {
    const matchesSearch = !searchTerm || 
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCategory = !selectedCategory || article.categoryName === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const renderArticleCard = (article: LearningModule) => (
    <Card key={article.id} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          {article.title}
        </Typography>
        
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Chip 
            icon={<CategoryIcon />} 
            label={article.categoryName || 'General'} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
          {article.viewCount && (
            <Typography variant="caption" color="text.secondary">
              {article.viewCount} views
            </Typography>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary" paragraph>
          {stripHtmlTags(article.content).substring(0, 150)}...
        </Typography>

        {article.tags && article.tags.length > 0 && (
          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
            {article.tags.map((tag, index) => (
              <Chip key={index} label={tag} size="small" variant="outlined" />
            ))}
          </Box>
        )}

        <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <Typography variant="caption" color="text.secondary">
            {article.authorName || 'Unknown'} • {format(new Date(article.createdAt), 'MMM dd, yyyy')}
          </Typography>
        </Box>
      </CardContent>

      <CardActions>
        <Button 
          size="small" 
          startIcon={<ViewIcon />}
          onClick={() => handleViewArticle(article)}
        >
          Read More
        </Button>
        {canManage && (
          <>
            <IconButton 
              size="small" 
              onClick={() => handleEditArticle(article)}
            >
              <EditIcon />
            </IconButton>
            <IconButton 
              size="small" 
              color="error"
              onClick={() => handleDeleteArticle(article.id)}
            >
              <DeleteIcon />
            </IconButton>
          </>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <IconButton onClick={goBack} edge="start">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Information & Resources
          </Typography>
        </Box>
        {canManage && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setFormData({ title: '', content: '', category: '', tags: '' });
              setSelectedArticle(null);
              setEditDialog(true);
            }}
          >
            Add Article
          </Button>
        )}
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {canManage && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
            <Tab label="Published" />
            <Tab label="Drafts" />
          </Tabs>
        </Box>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : filteredArticles.length === 0 ? (
        <Alert severity="info">
          No articles found matching your search criteria.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {filteredArticles.map((article) => (
            <Grid item xs={12} sm={6} md={4} key={article.id}>
              {renderArticleCard(article)}
            </Grid>
          ))}
        </Grid>
      )}

      {/* View Article Dialog */}
      <Dialog open={viewDialog} onClose={() => setViewDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedArticle?.title}</DialogTitle>
        <DialogContent>
          {selectedArticle && (
            <Box>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <Chip label={selectedArticle.categoryName || 'General'} color="primary" />
                <Typography variant="caption" color="text.secondary">
                  By {selectedArticle.authorName || 'Unknown'} • {format(new Date(selectedArticle.createdAt), 'MMMM dd, yyyy')}
                </Typography>
              </Box>
              
              <Box 
                dangerouslySetInnerHTML={{ __html: selectedArticle.content }}
                sx={{ 
                  '& p': { marginBottom: 2 },
                  '& ul, & ol': { marginLeft: 3 },
                  '& h1, & h2, & h3': { marginTop: 2, marginBottom: 1 }
                }}
              />
              
              {selectedArticle.tags && selectedArticle.tags.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mt={2}>
                  {selectedArticle.tags.map((tag, index) => (
                    <Chip key={index} label={tag} size="small" />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Edit/Create Article Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedArticle ? 'Edit Article' : 'Create Article'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  label="Category"
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags (comma separated)"
                value={formData.tags}
                onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                placeholder="mental health, wellness, support"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveArticle}>
            {selectedArticle ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InformationPage;